import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { auraStickerHoverRef } from '../../auraStickerHoverRef';
import '../../styles/AuraPromoSticker.css';

/* Strict Mode 이중 마운트 시에도 같은 스폰 위치 유지 */
let auraStickerSpawnAnchorCache = null;

/** 뷰포트 네 귀퉁이 중 하나 근처만 균등 랜덤 (중앙 구간 제외) */
function randomCornerStickerAnchor(horizRoom, vertRoom, margin) {
  const m = margin;
  const rand = (a, b) => (b <= a ? a : a + Math.random() * (b - a));
  const frac = 0.42;
  const hBand = horizRoom * frac;
  const vBand = vertRoom * frac;
  const leftMin = m;
  const leftMax = m + horizRoom;
  const topMin = m;
  const topMax = m + vertRoom;
  const corner = Math.floor(Math.random() * 4);

  switch (corner) {
    case 0:
      return {
        left: rand(leftMin, Math.min(leftMax, leftMin + hBand)),
        top: rand(topMin, Math.min(topMax, topMin + vBand)),
      };
    case 1:
      return {
        left: rand(Math.max(leftMin, leftMax - hBand), leftMax),
        top: rand(topMin, Math.min(topMax, topMin + vBand)),
      };
    case 2:
      return {
        left: rand(leftMin, Math.min(leftMax, leftMin + hBand)),
        top: rand(Math.max(topMin, topMax - vBand), topMax),
      };
    default:
      return {
        left: rand(Math.max(leftMin, leftMax - hBand), leftMax),
        top: rand(Math.max(topMin, topMax - vBand), topMax),
      };
  }
}

/** Navbar의 Aura / Work / Contact 메뉴 영역(다른 파일 수정 없이 DOM만 조회) */
function getNavbarMenuExclusionRect() {
  if (typeof document === 'undefined') return null;
  const menu = document.querySelector('.navbar-menu');
  if (!menu) return null;
  const r = menu.getBoundingClientRect();
  if (r.width < 2 || r.height < 2) return null;
  const pad = 8;
  return {
    left: r.left - pad,
    top: r.top - pad,
    right: r.right + pad,
    bottom: r.bottom + pad,
  };
}

/** 히어로 초기 카피(h1 Beyond Connection + 부제) — AboutSection의 .hero-content */
function getHeroContentExclusionRect() {
  if (typeof document === 'undefined') return null;
  const hero = document.querySelector('.hero-content');
  if (!hero) return null;
  const r = hero.getBoundingClientRect();
  if (r.width < 2 || r.height < 2) return null;
  const pad = 20;
  return {
    left: r.left - pad,
    top: r.top - pad,
    right: r.right + pad,
    bottom: r.bottom + pad,
  };
}

function getStickerExclusionRects() {
  return [getNavbarMenuExclusionRect(), getHeroContentExclusionRect()].filter(
    Boolean
  );
}

function stickerOverlapsAnyExclusion(left, top, w, h, exclusions) {
  for (let i = 0; i < exclusions.length; i++) {
    if (stickerOverlapsExclusion(left, top, w, h, exclusions[i])) return true;
  }
  return false;
}

function stickerOverlapsExclusion(left, top, w, h, ex) {
  if (!ex) return false;
  const nr = left + w;
  const nb = top + h;
  return !(nr <= ex.left || left >= ex.right || nb <= ex.top || top >= ex.bottom);
}

function pickSpawnAvoidingExclusions(horizRoom, vertRoom, m, w, h) {
  const exclusions = getStickerExclusionRects();
  const clear = (a) =>
    !stickerOverlapsAnyExclusion(a.left, a.top, w, h, exclusions);

  for (let i = 0; i < 48; i++) {
    const a = randomCornerStickerAnchor(horizRoom, vertRoom, m);
    if (clear(a)) return a;
  }
  const corner = Math.random() < 0.5 ? 2 : 3;
  for (let i = 0; i < 24; i++) {
    const a = randomCornerStickerAnchorForCorner(horizRoom, vertRoom, m, corner);
    if (clear(a)) return a;
  }
  return randomCornerStickerAnchor(horizRoom, vertRoom, m);
}

function randomCornerStickerAnchorForCorner(horizRoom, vertRoom, margin, corner) {
  const m = margin;
  const rand = (a, b) => (b <= a ? a : a + Math.random() * (b - a));
  const frac = 0.42;
  const hBand = horizRoom * frac;
  const vBand = vertRoom * frac;
  const leftMin = m;
  const leftMax = m + horizRoom;
  const topMin = m;
  const topMax = m + vertRoom;

  switch (corner) {
    case 2:
      return {
        left: rand(leftMin, Math.min(leftMax, leftMin + hBand)),
        top: rand(Math.max(topMin, topMax - vBand), topMax),
      };
    default:
      return {
        left: rand(Math.max(leftMin, leftMax - hBand), leftMax),
        top: rand(Math.max(topMin, topMax - vBand), topMax),
      };
  }
}

/**
 * 드래그 시 뷰포트 안 유지.
 * aside의 getBoundingClientRect()는 자식(bob)의 3D 회전·호버 scale·플로트 translateY 밖으로
 * 튀어나온 픽셀을 포함하지 않아, 좌·우·하단에 붙이면 잘려 보일 수 있음 → 패딩을 넉넉히 둠.
 */
function clampDragOffsetToViewport(r, cur, nx, ny, pad) {
  const vv = typeof window !== 'undefined' ? window.visualViewport : null;
  const vw = vv?.width ?? window.innerWidth;
  const vh = vv?.height ?? window.innerHeight;
  const vx = vv?.offsetLeft ?? 0;
  const vy = vv?.offsetTop ?? 0;

  let cx = nx;
  let cy = ny;
  const ddx = cx - cur.x;
  const ddy = cy - cur.y;
  let nl = r.left + ddx;
  let nt = r.top + ddy;
  let nr = nl + r.width;
  let nb = nt + r.height;

  const minL = vx + pad;
  const minT = vy + pad;
  const maxR = vx + vw - pad;
  const maxB = vy + vh - pad;

  if (nl < minL) cx += minL - nl;
  if (nt < minT) cy += minT - nt;
  if (nr > maxR) cx -= nr - maxR;
  if (nb > maxB) cy -= nb - maxB;

  return { x: cx, y: cy };
}

/** 스티커 현황 표시용 — 실제 집계·API 연동 시 이 값만 갱신하면 됩니다. */
const AURA_PROMO_STATS = {
  projectCount: 2,
  totalUseCount: 75,
};

const AuraPromoSticker = () => {
  /* 닫기는 이 탭·방문 동안만 유지; 새로고침하면 다시 표시 */
  const [open, setOpen] = useState(true);
  const [hover, setHover] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [anchor, setAnchor] = useState(null);
  const [dragging, setDragging] = useState(false);
  const asideRef = useRef(null);
  const bobRef = useRef(null);
  const offsetRef = useRef({ x: 0, y: 0 });
  const dragSessionRef = useRef(null);
  const openRef = useRef(open);
  const anchorRef = useRef(anchor);
  const draggingRef = useRef(false);

  const dismiss = () => {
    setHover(false);
    auraStickerHoverRef.current = 0;
    setOpen(false);
  };

  useEffect(() => {
    return () => {
      auraStickerHoverRef.current = 0;
    };
  }, []);

  useEffect(() => {
    if (!open) {
      auraStickerHoverRef.current = 0;
    }
  }, [open]);

  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    anchorRef.current = anchor;
  }, [anchor]);

  useEffect(() => {
    draggingRef.current = dragging;
  }, [dragging]);

  /**
   * CSS keyframes와 달리 매 프레임 스티커 중심 vs 뷰포트 중심 오프셋으로 rotateX/Y/Z를 갱신.
   * transform-origin은 화면 정중앙이 되도록 px로 맞춤. 드래그 중에는 위치 기반만(미세 플로트 끔).
   */
  useEffect(() => {
    if (!open) return;

    const bobForCleanup = bobRef.current;

    let rafId = 0;
    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const tick = () => {
      if (!openRef.current) return;

      const bob = bobRef.current;
      if (!bob || !anchorRef.current) {
        rafId = requestAnimationFrame(tick);
        return;
      }

      const r = bob.getBoundingClientRect();
      if (r.width < 2 || r.height < 2) {
        rafId = requestAnimationFrame(tick);
        return;
      }

      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const hw = vw * 0.5;
      const hh = vh * 0.5;
      const cx = r.left + r.width * 0.5;
      const cy = r.top + r.height * 0.5;
      let nx = (cx - hw) / Math.max(hw, 1);
      let ny = (cy - hh) / Math.max(hh, 1);
      nx = Math.max(-1, Math.min(1, nx));
      ny = Math.max(-1, Math.min(1, ny));

      const maxRx = 7;
      const maxRy = 8.5;
      const maxRz = 3.2;

      let rx = -ny * maxRx;
      let ry = nx * maxRy;
      let rz = -nx * maxRz - ny * 0.5;
      let ty = 0;

      if (!reduceMotion && !draggingRef.current) {
        const t = performance.now() * 0.001;
        ty = Math.sin(t * 1.1) * -4;
        rx += Math.sin(t * 0.85) * 0.6;
        ry += Math.cos(t * 0.72) * 0.5;
        rz += Math.sin(t * 0.93) * 0.85;
      }

      const ox = hw - r.left;
      const oy = hh - r.top;
      bob.style.transformOrigin = `${ox}px ${oy}px`;
      bob.style.transform = `translateY(${ty}px) rotateX(${rx}deg) rotateY(${ry}deg) rotateZ(${rz}deg)`;

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafId);
      if (bobForCleanup) {
        bobForCleanup.style.removeProperty('transform');
        bobForCleanup.style.removeProperty('transform-origin');
      }
    };
  }, [open]);

  useLayoutEffect(() => {
    const el = asideRef.current;
    if (!el) return;

    if (auraStickerSpawnAnchorCache) {
      setAnchor(auraStickerSpawnAnchorCache);
      return;
    }

    const rect = el.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    /* 초기 스폰도 실제 그려지는 영역(3D·스케일) 여유를 두고 뷰 안에 두기 */
    const m = 28;
    const horizRoom = Math.max(0, vw - w - 2 * m);
    const vertRoom = Math.max(0, vh - h - 2 * m);
    auraStickerSpawnAnchorCache = pickSpawnAvoidingExclusions(
      horizRoom,
      vertRoom,
      m,
      w,
      h
    );
    setAnchor(auraStickerSpawnAnchorCache);
  }, []);

  const onDragPointerDown = (e) => {
    if (e.button !== 0) return;
    const t = e.target;
    const el = t instanceof Element ? t : t.parentElement;
    if (el?.closest('a, button')) return;
    e.preventDefault();
    dragSessionRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      origX: offsetRef.current.x,
      origY: offsetRef.current.y,
    };
    setDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onDragPointerMove = (e) => {
    if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
    const s = dragSessionRef.current;
    const el = asideRef.current;
    if (!s || !el) return;

    let nx = s.origX + (e.clientX - s.startX);
    let ny = s.origY + (e.clientY - s.startY);
    const cur = offsetRef.current;
    const r = el.getBoundingClientRect();
    const edgePad = 46;
    const { x: cx, y: cy } = clampDragOffsetToViewport(r, cur, nx, ny, edgePad);

    offsetRef.current = { x: cx, y: cy };
    setOffset({ x: cx, y: cy });
  };

  const endDrag = (e) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    dragSessionRef.current = null;
    setDragging(false);
  };

  if (!open) {
    return null;
  }

  const setStickerHover = (on) => {
    if (on) {
      setHover(true);
      auraStickerHoverRef.current = 1;
    } else {
      setHover(false);
      auraStickerHoverRef.current = 0;
    }
  };

  const asideClass =
    (hover ? 'aura-promo-sticker aura-promo-sticker--hover' : 'aura-promo-sticker') +
    (dragging ? ' aura-promo-sticker--dragging' : '');

  return (
    <aside
      ref={asideRef}
      className={asideClass}
      style={{
        ...(anchor
          ? {
              left: anchor.left,
              top: anchor.top,
              right: 'auto',
              bottom: 'auto',
            }
          : { visibility: 'hidden' }),
        transform: `translate3d(${offset.x}px, ${offset.y}px, 0)`,
      }}
      aria-label="Aura 접속 및 운영 현황"
    >
      <div className="aura-promo-sticker__ambient" aria-hidden="true" />
      <div className="aura-promo-sticker__stage">
        <div
          ref={bobRef}
          className="aura-promo-sticker__bob"
          onMouseEnter={() => setStickerHover(true)}
          onMouseLeave={() => setStickerHover(false)}
          onPointerEnter={() => setStickerHover(true)}
          onPointerLeave={() => setStickerHover(false)}
          onPointerDown={onDragPointerDown}
          onPointerMove={onDragPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          <div className="aura-promo-sticker__void" aria-hidden="true" />
          <div className="aura-promo-sticker__shell">
            <div className="aura-promo-sticker__inner">
              <button
                type="button"
                className="aura-promo-sticker__close"
                onClick={dismiss}
                aria-label="스티커 닫기"
              >
                ×
              </button>
              <p className="aura-promo-sticker__eyebrow">BIIYEON · WebAR</p>
              <p className="aura-promo-sticker__title">Aura</p>
              <p className="aura-promo-sticker__copy">Aura 접속 · 운영 지표</p>
              <div
                className="aura-promo-sticker__stats"
                role="group"
                aria-label="Aura 운영 현황"
              >
                <div className="aura-promo-sticker__stat">
                  <div className="aura-promo-sticker__stat-label">프로젝트</div>
                  <div className="aura-promo-sticker__stat-value">
                    {AURA_PROMO_STATS.projectCount}
                    <span className="aura-promo-sticker__stat-unit">개</span>
                  </div>
                </div>
                <div className="aura-promo-sticker__stat">
                  <div className="aura-promo-sticker__stat-label">누적 실행</div>
                  <div className="aura-promo-sticker__stat-value">
                    {AURA_PROMO_STATS.totalUseCount.toLocaleString('ko-KR')}
                    <span className="aura-promo-sticker__stat-unit">회</span>
                  </div>
                </div>
              </div>
              <a href="https://cms.biiyeon.com" className="aura-promo-sticker__cta">
                <span className="aura-promo-sticker__cta-shine" aria-hidden="true" />
                <span className="aura-promo-sticker__cta-label">접속하기</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default AuraPromoSticker;
